import React, { useState, useEffect } from 'react';
import { Modal, Select, Input, Form, Tag, Spin, Alert, message } from 'antd';
import { UserOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { affectationService, sansComptesEditeur } from '../../../services/affectationService';
import { CHARTE_COLORS as C } from '../../../variables/constants';
import { filtreSansAccents } from '../../../utils/rechercheTexte';

const { TextArea } = Input;

/**
 * Affecter (ou réaffecter) un dossier à un agent.
 *
 * La liste montre la charge actuelle de chacun : le donneur d'ordre voit qui est déjà
 * chargé avant de choisir, plutôt que de toujours confier au même.
 *
 * Les agents qui suivent le bureau homologue du dossier sont SIGNALÉS, jamais
 * isolés : la liste reste complète et dans le même ordre. C'est ce qui permet de
 * couvrir une absence sans rien reparamétrer - le bureau oriente le choix, il ne
 * le contraint pas.
 */
export default function AffecterDossierModal({
  open,
  onClose,
  sinistre,          // { sinistreTrackingId, numeroSinistreLocal, redacteurNomPrenom }
  // Plusieurs dossiers d'un coup (sélection dans les affectations en attente) :
  // chacun est confié au même agent, avec la même consigne.
  sinistres = null,
  onAffecte,         // callback après succès
  // L'agent qui traite le dossier le confie lui-même à un collègue : motif
  // obligatoire, route dédiée (pas besoin de SINISTRE_ASSIGN).
  parTitulaire = false,
  // L'agent qui traite déjà le dossier : le backend refuse de le lui réaffecter,
  // autant ne pas le proposer.
  exclureTrackingId = null,
}) {
  const { t } = useTranslation('dossierSinistre');
  const [form] = Form.useForm();
  const [agents, setAgents] = useState([]);
  const [chargement, setChargement] = useState(false);
  const [envoi, setEnvoi] = useState(false);

  const lot = sinistres && sinistres.length ? sinistres : null;
  // Le repère « suit ce bureau » n'a de sens que pour un dossier : pour un lot, la
  // liste est demandée sans dossier.
  const dossierDeReference = lot ? (lot.length === 1 ? lot[0] : null) : sinistre;

  useEffect(() => {
    if (!open) return;
    form.resetFields();
    setChargement(true);
    affectationService.utilisateursAffectables(dossierDeReference?.sinistreTrackingId)
      .then((r) => setAgents(sansComptesEditeur(r.data?.data)
        .filter((a) => a.utilisateurTrackingId !== exclureTrackingId)))
      .catch(() => setAgents([]))
      .finally(() => setChargement(false));
  }, [open, form, dossierDeReference?.sinistreTrackingId, exclureTrackingId]);

  /* Lot : un appel par dossier. Chacun garde ses contrôles serveur (déjà pris par
     un collègue entre-temps, par exemple) : un refus n'arrête pas les suivants, et
     le bilan dit lesquels n'ont pas été affectés. */
  const validerLot = async (v) => {
    setEnvoi(true);
    const refuses = [];
    let faits = 0;
    for (const d of lot) {
      try {
        await affectationService.affecter(d.sinistreTrackingId, {
          redacteurTrackingId: v.redacteurTrackingId,
          consigne: v.consigne?.trim() || null,
        });
        faits += 1;
      } catch (e) {
        refuses.push(`${d.numeroSinistreLocal || d.sinistreTrackingId} : ${e?.response?.data?.message || t('affecterDossier.refuse')}`);
      }
    }
    setEnvoi(false);
    if (faits > 0) message.success(t('affecterDossier.lotAffectes', { count: faits }));
    if (refuses.length) message.warning({ content: t('affecterDossier.lotNonAffectes', { count: refuses.length, liste: refuses.join(' · ') }), duration: 8 });
    onAffecte?.();
    onClose();
  };

  const valider = async () => {
    const v = await form.validateFields();
    if (lot) {
      await validerLot(v);
      return;
    }
    setEnvoi(true);
    try {
      const r = parTitulaire
        ? await affectationService.reaffecter(sinistre.sinistreTrackingId, {
          redacteurTrackingId: v.redacteurTrackingId,
          motif: v.consigne?.trim(),
        })
        : await affectationService.affecter(sinistre.sinistreTrackingId, {
          redacteurTrackingId: v.redacteurTrackingId,
          consigne: v.consigne?.trim() || null,
        });
      message.success(r.data?.message || t('affecterDossier.dossierAffecte'));
      onAffecte?.(r.data?.data);
      onClose();
    } catch (e) {
      // Le backend renvoie un message métier explicite (déjà affecté au même agent…).
      message.error(e?.response?.data?.message || t('affecterDossier.echec'));
    } finally {
      setEnvoi(false);
    }
  };

  const reaffectation = !lot && (parTitulaire || !!sinistre?.redacteurNomPrenom);

  return (
    <Modal
      open={open}
      onCancel={onClose}
      onOk={valider}
      okText={reaffectation ? t('affecterDossier.reaffecter') : t('affecterDossier.affecter')}
      cancelText={t('affectation.annuler')}
      confirmLoading={envoi}
      title={
        <span>
          <UserOutlined style={{ color: C.greenMid, marginRight: 8 }} />
          {lot && lot.length > 1 ? t('affecterDossier.titreLot', { n: lot.length })
            : reaffectation ? t('affecterDossier.titreReaffecter') : t('affecterDossier.titreAffecter')}
          {dossierDeReference?.numeroSinistreLocal && (
            <span style={{ color: C.muted, fontWeight: 400 }}> · {dossierDeReference.numeroSinistreLocal}</span>
          )}
        </span>
      }
      destroyOnClose
    >
      {lot && lot.length > 1 && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message={t('affecterDossier.lotMemeAgent', { n: lot.length })}
          description={lot.map((d) => d.numeroSinistreLocal).filter(Boolean).join(', ')}
        />
      )}
      {parTitulaire ? (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message={t('affecterDossier.vousTraitez')}
          description={t('affecterDossier.vousTraitezDetail')}
        />
      ) : reaffectation && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message={t('affecterDossier.traitePar', { nom: sinistre.redacteurNomPrenom })}
          description={t('affecterDossier.traiteParDetail')}
        />
      )}

      <Spin spinning={chargement}>
        <Form form={form} layout="vertical" requiredMark={false}>
          <Form.Item
            name="redacteurTrackingId"
            label={t('affectation.confierA')}
            rules={[{ required: true, message: t('affectation.choisissezAgent') }]}
          >
            <Select
              showSearch
              filterOption={filtreSansAccents}
              placeholder={t('affectation.rechercherAgent')}
              optionFilterProp="label"
              options={agents.map((a) => ({
                value: a.utilisateurTrackingId,
                // L'e-mail entre dans le libellé de recherche : deux agents peuvent
                // être homonymes, et c'est lui qui les départage.
                label: `${a.nomPrenom} ${a.email || ''}`,
                agent: a,
              }))}
              optionRender={({ data }) => {
                const a = data.agent;
                return (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                    <span style={{ minWidth: 0 }}>
                      <span>
                        {a.nomPrenom}
                        {a.gereLeBureau && (
                          <Tag color="green" style={{ borderRadius: 12, fontSize: 10, marginLeft: 6 }}>
                            {t('affecterDossier.suitCeBureau')}
                          </Tag>
                        )}
                        {a.profilNom && (
                          <span style={{ color: C.faint, fontSize: 12, marginLeft: 6 }}>{a.profilNom}</span>
                        )}
                      </span>
                      {a.email && (
                        <div style={{
                          fontSize: 11, color: C.faint,
                          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        }}>
                          {a.email}
                        </div>
                      )}
                    </span>
                    <span style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                      <Tag color={a.dossiersEnCours > 15 ? 'red' : a.dossiersEnCours > 8 ? 'orange' : 'green'}
                        style={{ margin: 0, fontSize: 11 }}>
                        {t('affectation.nbDossiers', { count: a.dossiersEnCours })}
                      </Tag>
                      {a.dossiersEnAttentePriseEnCharge > 0 && (
                        <Tag color="gold" style={{ margin: 0, fontSize: 11 }}>
                          {t('affecterDossier.aPrendre', { n: a.dossiersEnAttentePriseEnCharge })}
                        </Tag>
                      )}
                    </span>
                  </div>
                );
              }}
            />
          </Form.Item>

          <Form.Item
            name="consigne"
            label={parTitulaire ? t('affecterDossier.motifReaffectation') : t('affectation.consigneFacultatif')}
            extra={t('affecterDossier.consigneAide')}
            rules={parTitulaire ? [
              { required: true, whitespace: true, message: t('affecterDossier.motifObligatoire') },
              { min: 5, message: t('affecterDossier.motifMin') },
            ] : []}
          >
            <TextArea
              rows={3}
              maxLength={1000}
              showCount
              placeholder={parTitulaire
                ? t('affecterDossier.motifPlaceholder')
                : t('affecterDossier.consignePlaceholder')}
            />
          </Form.Item>
        </Form>
      </Spin>
    </Modal>
  );
}
